/**
 * ENG-8186: identify WHICH claude.ai account the host is currently signed in as.
 *
 * The manager already restarts a session when the auth *tuple* changes
 * (`auth-tuple-change` in manager-worker.ts), but in subscription mode that
 * tuple is the constant string `subscription:none` — `anthropicApiKeyFingerprint`
 * is only populated in api_key mode. So an operator resolving a Claude Code usage
 * limit by logging into a DIFFERENT account changed nothing the manager watches:
 * the running `claude` kept the old, capped credentials in memory (they are only
 * synced to /root at spawn), and the ENG-7904 usage-limit marker — which makes
 * every channel MCP answer with a canned "hit its limit" notice INSTEAD of
 * dispatching to the tmux pane — was never cleared. The agent stayed unreachable.
 *
 * This module supplies the missing signal: a short, non-secret fingerprint of the
 * signed-in account. It is deliberately derived from `oauthAccount` in
 * `.claude.json` rather than from the tokens in `.credentials.json`, because
 * Claude Code silently refreshes the access token roughly every 8 hours — keying
 * on a token would respawn every agent on the host three times a day. The account
 * uuid changes only on a real login.
 *
 * Resolution mirrors the credentials the spawn will actually use
 * (`findClaudeCredentialsPaths` / `syncClaudeCredsToRoot`): the account is read
 * from the home directory whose credentials file `claude` will pick up, so the
 * fingerprint always describes the account the AGENT runs as — not some other
 * account that happens to be logged in elsewhere on the box.
 */

import { createHash } from 'node:crypto';
import { readFile, readdir } from 'node:fs/promises';
import { homedir, platform } from 'node:os';
import { dirname, join } from 'node:path';
import { findClaudeCredentialsPaths } from './claude-auth-detect.js';

/**
 * Home directories that may hold a Claude Code login, in the same preference
 * order as {@link findClaudeCredentialsPaths}: the calling user's home first,
 * then `/home/*` when the manager runs as Linux root (operators log in as
 * ssm-user / ec2-user, leaving creds under their own home).
 */
async function candidateHomes(): Promise<string[]> {
  const homes: string[] = [homedir()];

  const isLinuxRoot =
    platform() === 'linux'
    && typeof process.getuid === 'function'
    && process.getuid() === 0;

  if (isLinuxRoot) {
    try {
      const entries = await readdir('/home', { withFileTypes: true });
      // Sorted so the fingerprint is deterministic across manager restarts —
      // readdir order is filesystem-dependent, and a flapping home order would
      // look like an account switch. `findClaudeCredentialsPaths` sorts its own
      // /home walk for the same reason; both orderings feed this module, so both
      // have to be stable.
      for (const entry of entries.filter((e) => e.isDirectory()).sort((a, b) => a.name.localeCompare(b.name))) {
        homes.push(join('/home', entry.name));
      }
    } catch {
      // /home doesn't exist or isn't readable — the calling user's home is all
      // we have, which is the normal case off Linux-root.
    }
  }

  return homes;
}

/**
 * The home whose credentials the spawned `claude` will use, or null when no
 * credentials file exists anywhere. On macOS the credentials live in the
 * Keychain rather than on disk, so this legitimately returns null and the
 * caller falls back to the calling user's home.
 */
async function homeOfActiveCredentials(): Promise<string | null> {
  for (const path of await findClaudeCredentialsPaths()) {
    try {
      await readFile(path, 'utf-8');
      // <home>/.claude/<credentials file> → <home>
      return dirname(dirname(path));
    } catch {
      // Not this one — keep looking.
    }
  }
  return null;
}

/**
 * The `.claude.json` files to inspect, most-authoritative first: the home whose
 * credentials are live, then every candidate home as a fallback (covers macOS
 * Keychain logins, which leave no credentials file on disk).
 *
 * `CLAUDE_CONFIG_DIR` is honoured first when set — best-effort support for
 * relocated Claude Code configs; a missing file there just falls through.
 */
export async function claudeConfigCandidatePaths(): Promise<string[]> {
  const paths: string[] = [];

  const configDir = process.env['CLAUDE_CONFIG_DIR']?.trim();
  if (configDir) paths.push(join(configDir, '.claude.json'));

  const activeHome = await homeOfActiveCredentials();
  if (activeHome) paths.push(join(activeHome, '.claude.json'));

  for (const home of await candidateHomes()) {
    const path = join(home, '.claude.json');
    if (!paths.includes(path)) paths.push(path);
  }

  return paths;
}

/**
 * Hash the account identity. The uuids aren't secrets, but the tuple is written
 * to manager.log on every change, so hashing keeps account identifiers out of
 * operator-facing logs while staying stable and comparable.
 */
function hashAccount(accountUuid: string, organizationUuid: string | null): string {
  return createHash('sha256')
    .update(`claude-account:${accountUuid}:${organizationUuid ?? ''}`)
    .digest('hex')
    .slice(0, 12);
}

/**
 * Read the signed-in account fingerprint from disk, or null when no account can
 * be resolved (never logged in, unreadable config, or a Claude Code version that
 * doesn't record `oauthAccount`). Never throws.
 *
 * `paths` is injectable for tests.
 */
export async function detectClaudeAccountFingerprint(
  paths?: readonly string[],
): Promise<string | null> {
  const candidates = paths ?? (await claudeConfigCandidatePaths());

  for (const path of candidates) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(await readFile(path, 'utf-8'));
    } catch {
      continue; // missing or malformed — try the next candidate
    }
    if (typeof parsed !== 'object' || parsed === null) continue;

    const account = (parsed as { oauthAccount?: unknown }).oauthAccount;
    if (typeof account !== 'object' || account === null) continue;

    const accountUuid = (account as { accountUuid?: unknown }).accountUuid;
    if (typeof accountUuid !== 'string' || accountUuid.trim().length === 0) continue;

    const organizationUuid = (account as { organizationUuid?: unknown }).organizationUuid;
    return hashAccount(
      accountUuid.trim(),
      typeof organizationUuid === 'string' && organizationUuid.trim().length > 0
        ? organizationUuid.trim()
        : null,
    );
  }

  return null;
}

// Last successfully-detected fingerprint. Sticky ON PURPOSE: a transient read
// failure (config being rewritten mid-read, a permissions blip) must not make the
// auth tuple flap value → unknown → value, which would respawn every agent on the
// host for no reason. Only a *different* successfully-read account moves it.
let lastKnownFingerprint: string | null = null;
let lastDetectAtMs = 0;

// The account is host-wide but the auth tuple is computed per agent per poll, so
// without a TTL a 10-agent host re-walks /home and re-reads every config on every
// cycle. The signal is a human logging in — a minute of latency is immaterial,
// and it matches the banner monitor's own cadence.
const DETECT_TTL_MS = 60_000;

/** Test helper — clear the sticky cache. Not for production use. */
export function _resetClaudeAccountFingerprintCache(): void {
  lastKnownFingerprint = null;
  lastDetectAtMs = 0;
}

/**
 * The signed-in account fingerprint for use in the manager's auth tuple: the
 * freshly-detected value when one can be read, otherwise the last known-good one
 * (see the sticky-cache note above). Null only until the first successful read.
 *
 * Passing `paths` explicitly (tests) bypasses the TTL so each call really reads.
 */
export async function getClaudeAccountFingerprint(
  paths?: readonly string[],
  nowMs: number = Date.now(),
): Promise<string | null> {
  if (!paths && lastKnownFingerprint !== null && nowMs - lastDetectAtMs < DETECT_TTL_MS) {
    return lastKnownFingerprint;
  }
  const detected = await detectClaudeAccountFingerprint(paths);
  if (!paths) lastDetectAtMs = nowMs;
  if (detected) lastKnownFingerprint = detected;
  return lastKnownFingerprint;
}
